/**
 * リード一覧 (マーケロール用)。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §6.1
 *
 * mail_contacts を `promotionLevel === 'lead'` でフィルタ。
 * tag 未設定 (undefined) も lead 扱い (後方互換)。
 */
import type { ContactKind, MailContact } from '@goenhub/shared';
import { Building2, Search, Sparkles, UserCircle2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useMailContacts } from '@/hooks/useMailContacts';

interface ContactWithMeta extends MailContact {
  id: string;
}

const KIND_LABEL: Record<ContactKind, string> = {
  btob: 'BtoB',
  btoc: 'BtoC',
  unknown: 'Unknown',
};

export function LeadsPage() {
  const { data: contacts, isLoading } = useMailContacts({ limit: 1000 });
  const [keyword, setKeyword] = useState('');

  const filtered = useMemo(() => {
    if (!contacts) return [] as ContactWithMeta[];
    const k = keyword.trim().toLowerCase();
    return contacts.filter((c) => {
      const level = c.promotionLevel ?? 'lead';
      if (level !== 'lead') return false;
      if (!k) return true;
      const hay = `${c.name ?? ''} ${c.defaultEmail ?? ''}`.toLowerCase();
      return hay.includes(k);
    }) as ContactWithMeta[];
  }, [contacts, keyword]);

  return (
    <Screen
      title="リード"
      description="マーケ段階 (promotionLevel='lead') の連絡先のみ表示。資料請求のみで完結したリードや、フォーム未送信の連絡先など。"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-[var(--text-muted)]" />
          <Input
            type="text"
            placeholder="氏名・メールで検索"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {isLoading ? (
        <Spinner />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Sparkles}
          title={
            (contacts?.length ?? 0) === 0
              ? '連絡先が登録されていません'
              : 'リード段階の連絡先がいません'
          }
          description="フォーム送信や CSV 取込で mail_contacts が増えるとここに表示されます。"
        />
      ) : (
        <div className="rounded-md border border-[var(--border)] overflow-hidden">
          <div className="grid grid-cols-[2fr_1.5fr_0.7fr_0.7fr] gap-3 px-4 py-2 bg-[var(--bg-elevated)] border-b border-[var(--border)] text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
            <div>氏名</div>
            <div>メール</div>
            <div>顧客タイプ</div>
            <div>タグ</div>
          </div>
          {filtered.map((c, i) => (
            <div
              key={c.id}
              className={`grid grid-cols-[2fr_1.5fr_0.7fr_0.7fr] gap-3 px-4 py-2 items-center ${
                i < filtered.length - 1 ? 'border-b border-[var(--border)]' : ''
              }`}
            >
              <div className="min-w-0 flex items-center gap-1.5">
                {c.kind === 'btob' ? (
                  <Building2 className="size-3.5 text-[var(--accent)] flex-shrink-0" />
                ) : (
                  <UserCircle2 className="size-3.5 text-[var(--accent-purple)] flex-shrink-0" />
                )}
                <span className="text-sm font-medium truncate">{c.name}</span>
              </div>
              <div className="text-xs text-[var(--text-secondary)] truncate">
                {c.defaultEmail}
              </div>
              <div>
                {c.kind ? <Badge variant="default">{KIND_LABEL[c.kind]}</Badge> : '—'}
              </div>
              <div className="flex flex-wrap gap-1">
                {(c.tags ?? []).slice(0, 3).map((t) => (
                  <Badge key={t} variant="default">
                    {t}
                  </Badge>
                ))}
                {(c.tags?.length ?? 0) > 3 && (
                  <Badge variant="default">+{(c.tags?.length ?? 0) - 3}</Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Screen>
  );
}
